import React, { HTMLAttributes } from 'react';

import {
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';

import { useTranslation } from 'react-i18next';
import { Button, Popup } from '../../../components';
import { ImmutableParagraphOnboardingQuestionEntity } from '..';
import useClasses from '../../../hooks/useClasses';

import './style.scss';
import usePopup from '../../../hooks/usePopup';

interface Props extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableParagraphOnboardingQuestionEntity;
  setCountQuestions: (number) => void;
  countQuestions: number;
  instanceOfParagraph: number;
  isInvalidSubmitAttempted: boolean;
}

const ParagraphOnboardingQuestion: React.FC<Props> = ({
  entity,
  setCountQuestions,
  countQuestions,
  instanceOfParagraph,
  isInvalidSubmitAttempted,
}) => {
  const { buildClass } = useClasses('ParagraphOnboardingQuestion');
  const { t } = useTranslation();

  const popup = usePopup('onboarding_popup', {
    closeOnOutsideClick: true,
    closeOnEscape: true,
    restoreFocusOnClose: true,
  });

  const [value, setValue] = React.useState<'yes' | 'no' | null>(null);
  const question = entity.getIn(['field_title', 0, 'value'], '');
  const bodyExplainer = entity.getIn(['field_text', 0, 'value'], '');
  const titleExplainer = entity.getIn(['field_title_explainer', 0, 'value'], '');
  const linkTitle = String(entity.getIn(['field_link', 0, 'title'], ''));
  const linkUri = String(entity.getIn(['field_link', 0, 'alias'], entity.getIn(['field_link', 0, 'uri'], '')));

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (instanceOfParagraph === countQuestions) {
      setCountQuestions(countQuestions + 1);
    }

    if (e.target.value === 'yes') {
      popup.open();

      setValue(e.target.value);

      return;
    }

    if (e.target.value === 'no') {
      setValue(e.target.value);
    }
  }

  const shouldShowError = (isInvalidSubmitAttempted && !value);

  const errorMessageId = `error-onboarding-${entity.getIn(['id', 0, 'value'])}`;

  return (
    <div className={buildClass()}>
      {popup.isOpen && (
        <Popup
          title={titleExplainer}
          closeButtonText={t('pages.onboarding.question_modal.close')}
          // eslint-disable-next-line react/no-unstable-nested-components
          cta={(linkTitle && linkUri) && (() => (
            <Button secondarySmall icon="outbound-link" className={buildClass('button')} href={linkUri} onClick={() => popup.close()}>
              {linkTitle}
            </Button>
          ))}
          {...popup.popupProps}
        >
          <WysiwygField
            property="schema:text"
            className={buildClass('body-explainer')}
            html={bodyExplainer}
          />
        </Popup>
      )}
      <fieldset aria-describedby={shouldShowError && errorMessageId}>
        <legend className={buildClass('question')}>{question}</legend>
        <div className={buildClass('button-container')}>
          <div className={buildClass('radio')}>
            <input
              type="radio"
              name={`onboarding-question-${entity.getIn(['id', 0, 'value'])}`}
              id={`onboarding-question-${entity.getIn(['id', 0, 'value'])}-yes`}
              value="yes"
              onChange={handleChange}
            />
            <label htmlFor={`onboarding-question-${entity.getIn(['id', 0, 'value'])}-yes`}>
              {t('pages.onboarding.question.yes')}
            </label>
          </div>
          <div className={buildClass('radio')}>
            <input
              type="radio"
              name={`onboarding-question-${entity.getIn(['id', 0, 'value'])}`}
              id={`onboarding-question-${entity.getIn(['id', 0, 'value'])}-no`}
              value="no"
              onChange={handleChange}
            />
            <label htmlFor={`onboarding-question-${entity.getIn(['id', 0, 'value'])}-no`}>
              {t('pages.onboarding.question.no')}
            </label>
          </div>
        </div>
        {shouldShowError && (
          <div className="FormElement__error" id={errorMessageId} aria-live="assertive">
            {t('pages.onboarding.required')}
          </div>
        )}
      </fieldset>
    </div>
  );
};

export default ParagraphOnboardingQuestion;
